import { format, parseISO } from 'date-fns';

import { addDollarSymbol } from './addDollarSymbol';
import { typeCheck } from './typeCheck';

// TableReferrals expects the same properties for statement and payment rows
// Only pass in custom data specific to each component

// fn must return type Object
export const normalizeReferralsTableData = fn => (data = []) =>
  data.map(d => ({
    ...d,
    statement_month: d.overview_month || '-',
    statement_date: (d.overview_endDate && d.overview_endDate.replace(/-/g, '/')) || '-',
    statement_balance:
      (d.overview_balance && addDollarSymbol(parseFloat(d.overview_balance).toFixed(2))) || '$0.00',
    payment_date: (d.created_at && format(parseISO(d.created_at), 'MM/dd/yyyy')) || '-',
    payment_amount:
      (d.referral_value && addDollarSymbol(parseFloat(d.referral_value).toFixed(2))) || '$0.00',
    payment_balance: (d.balance && addDollarSymbol(parseFloat(d.balance).toFixed(2))) || '$0.00',
    ...fn(d),
  }));

export const defaultAdminReferralsCsvCb = d => ({
  referrer: (d.referrer && d.referrer.company_name) || '-',
  customer: (d.customer && d.customer.company_name) || '-',
  date: (d.created_at && format(parseISO(d.created_at), 'MM/dd/yyyy')) || '-',
  shopify_reward: d.is_shopify_sale === 1 ? 'YES' : 'NO',
  sales_order: d.order_number || ' - ',
  invoice_memo_number: d.invoice_memo_number || '-',
  'Sales Order #': d.order_number || '-',
  order_total: (d.amount && addDollarSymbol(parseFloat(d.amount).toFixed(2))) || '-',
  percentage_earned:
    (!d.is_shopify_sale
      ? d.machine_type_referral_tag_value && `${d.machine_type_referral_tag_value}%`
      : `${d.referral_payout_tag.shop_products_reward}%`) || '-',
  referral_value:
    (d.referral_value && addDollarSymbol(parseFloat(d.referral_value).toFixed(2))) || '$0.00',
  type: (d.type && d.type) || '-',
  balance: (d.balance && addDollarSymbol(parseFloat(d.balance).toFixed(2))) || '$0.00',
});

export const defaultAdminReferralsCb = d => ({
  'person referrerd': (d.customer && d.customer.company_name) || '-',
  'order details': (d.created_at && format(parseISO(d.created_at), 'MM/dd/yyyy')) || '-',
  'date created': (d.created_at && format(parseISO(d.created_at), 'MM/dd/yyyy')) || '-',
  'shopify reward': d.is_shopify_sale && d.is_shopify_sale === 1 ? 'YES' : 'NO' || '-',
  'sales order': d.order_number || '-',
  'invoice/memo #': d.invoice_memo_number || '-',
  'Sales Order #': d.order_number || '-',
  'order total': (d.amount && addDollarSymbol(parseFloat(d.amount).toFixed(2))) || '$0.00',
  'percentage earned':
    (!d.is_shopify_sale
      ? d.machine_type_referral_tag_value && `${d.machine_type_referral_tag_value}%`
      : `${d.referral_payout_tag.shop_products_reward}%`) || '-',
  'referral value':
    (d.referral_value && addDollarSymbol(parseFloat(d.referral_value).toFixed(2))) || '$0.00',
  'total balance': (d.balance && addDollarSymbol(parseFloat(d.balance).toFixed(2))) || '$0.00',
});

export const defaultAdminReferrersCb = data => ({
  id: data.id,
  "referrer's name": data.first_name + ' ' + data.last_name || ' - ',
  "referrer's tag type": data.referral_payout_tag.name || ' - ',
  'sales rep': data.sales_rep_name || '-',
  'phone number': data.phone || ' - ',
  email: data.email || ' - ',
  'total balance':
    (data.balance && addDollarSymbol(parseFloat(data.balance).toFixed(2))) || '$0.00',
  ...data,
});

// 'referral threshold': (d.referral_payout_tag && d.referral_payout_tag.minimum_referrals) || '-',
export const defaultCustomerReferralsCb = d => ({
  customer: (d.customer && d.customer.first_name) || '-',
  'invoice/memo #': d.invoice_memo_number || '-',
  'Sales Order #': d.order_number || '-',
  'date created': (d.created_at && format(parseISO(d.created_at), 'MM/dd/yyyy')) || '-',
  'shopify reward': d.is_shopify_sale && d.is_shopify_sale === 1 ? 'YES' : 'NO' || '-',
  'referral threshold':
    d.referral_payout_tag && d.orders_count && d.orders_count > 0
      ? d.orders_count + '/' + d.referral_payout_tag.minimum_referrals
      : d.referral_payout_tag
      ? d.referral_payout_tag.minimum_referrals
      : '-',
  'order total': (d.amount && addDollarSymbol(parseFloat(d.amount).toFixed(2))) || '-',
  'percentage earned':
    (!d.is_shopify_sale
      ? d.machine_type_referral_tag_value && `${d.machine_type_referral_tag_value}%`
      : `${d.referral_payout_tag.shop_products_reward}%`) || '-',
  'rewards amount':
    (d.referral_value && addDollarSymbol(parseFloat(d.referral_value).toFixed(2))) || '$0.00',
  'total balance': (d.balance && addDollarSymbol(parseFloat(d.balance).toFixed(2))) || '$0.00',
});

export const normalizeAdminReferrals = data =>
  typeCheck(data).isArray && data.length > 0
    ? normalizeReferralsTableData(defaultAdminReferralsCb)(data)
    : [];

export const normalizeAdminReferrers = (data = [], fn = d => d) =>
  data.map(d => ({
    ...fn(d),
    ...defaultAdminReferrersCb(d),
  }));

export const normalizeAdminReferralsCsv = data =>
  normalizeReferralsTableData(defaultAdminReferralsCsvCb)(data);

export const normalizeCustomerReferrals = data => {
  return normalizeReferralsTableData(defaultCustomerReferralsCb)(data);
};
